import { css } from "@emotion/css";
import type { PageAction, PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { ListItemLink, ListItemContent, Pagination, SearchInput, List, EmptyStateNoResults } from "@octopusdeploy/design-system-components";
import { borderRadius, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { Repository, ResourceCollection, TagSetResource } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { links, type QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { sortBy } from "lodash";
import { useCallback, useEffect } from "react";
import * as React from "react";
import useIsMultiTenancyEnabledFeatureFlag from "~/areas/configuration/hooks/useIsMultiTenancyEnabledFeatureFlag";
import Dialog from "~/components/Dialog/Dialog";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import { PortalEmptyStateOnboarding } from "~/components/GettingStarted/PortalEmptyStateOnboarding";
import MarkdownDescription from "~/components/MarkdownDescription";
import ExternalLink from "~/components/Navigation/ExternalLink/index";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import Tag from "~/components/Tag/index";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
import ListTitle from "~/primitiveComponents/dataDisplay/ListTitle";
import TagSetsSorter from "./TagSetsSorter";
import styles from "./style.module.less";
interface TagSetsPageProps {
    spaceId: string;
    queryParams: TagSetsPageQueryParams;
    setQueryParams: QueryParamValuesSetter<TagSetsPageQueryParams>;
    loaderData: TagSetsPageLoaderData;
}
const pageTitle = "Tenant Tag Sets";
export interface TagSetsPageQueryParams {
    tagSetName: string;
    page: number | undefined;
    pageSize: number | undefined;
}
interface TagSetsPageLoaderData {
    tagSets: ResourceCollection<TagSetResource>;
    hasTagSets: boolean;
}
const defaultPageSize = 30;
const pageSizeOptions = [defaultPageSize, 50, 100];
export async function tagSetsPageLoader(repository: Repository, queryParams: TagSetsPageQueryParams): Promise<TagSetsPageLoaderData> {
    return await getPaginatedTagSets(repository, queryParams);
}
export function TagSetsPage({ spaceId, queryParams, setQueryParams, loaderData }: TagSetsPageProps) {
    const navigation = useSpaceAwareNavigation();
    const isMultiTenancyEnabled = useIsMultiTenancyEnabledFeatureFlag();
    const sortDialogControls = useDialogTrigger();
    const [debouncedVariableSetName, setDebouncedVariableSetName] = useDebouncedExternalState(queryParams.tagSetName, (value) => setQueryParams({ tagSetName: value, page: 1 }), 200);
    const { result: pagedTagSetsQueryResult, refetch: reloadTagSets } = usePagedTagSetsQuery(queryParams, loaderData);
    const { tagSets, hasTagSets } = pagedTagSetsQueryResult;
    const onPageChange = useCallback((page: number) => {
        setQueryParams({ ...queryParams, page });
    }, [queryParams, setQueryParams]);
    const onPageSizeChange = useCallback((pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize, page: 1 });
    }, [queryParams, setQueryParams]);
    useEffect(() => {
        if (!isMultiTenancyEnabled) {
            navigation.redirect(links.tenantsPage.generateUrl({ spaceId }));
        }
    }, [isMultiTenancyEnabled, navigation, spaceId]);
    const addTagSetPageAction: PrimaryPageAction = {
        type: "navigate",
        hasPermissions: isAllowed({ permission: Permission.TagSetCreate }),
        label: "Add Tag Set",
        path: links.createTagSetPage.generateUrl({ spaceId }),
    };
    if (!hasTagSets) {
        return (<PortalEmptyStateOnboarding title={"Create tenant tags to easily classify and group tenants"} primaryAction={addTagSetPageAction} description={"Tenant tags help you to classify your tenants using custom tags that meet your needs, and tailor tenanted deployments for your projects and environments."} learnMore={<ExternalLink href="MultiTenantTags">Learn more</ExternalLink>}/>);
    }
    const hideBottomPagination = tagSets.Items.length < 20;
    return (<PageContent pagination={{
            ui: (<Pagination label="Tag Sets" totalResults={tagSets.TotalResults} itemsPerPageOptions={pageSizeOptions} selectedItemsPerPage={tagSets.ItemsPerPage} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange}/>),
            placement: hideBottomPagination ? "top" : "topAndBottom",
        }} header={{ title: pageTitle, primaryAction: addTagSetPageAction, pageActions: getPageActions(sortDialogControls.openDialog) }} filters={{ inputs: [<SearchInput value={debouncedVariableSetName} onChange={setDebouncedVariableSetName} accessibleName={"Search by tag set name"} placeholder={"Filter by name..."}/>] }}>
            <ContentBorder>
                <Dialog open={sortDialogControls.isOpen}>
                    <TagSetsSorter onSaved={reloadTagSets}/>
                </Dialog>
                <List items={tagSets.Items} renderRow={({ item }) => <TagSetListItem spaceId={spaceId} tagSet={item}/>} rowKey={(item) => item.Id} empty={<EmptyStateNoResults />}/>
            </ContentBorder>
        </PageContent>);
}
interface GetPaginatedTagSetsOptions {
    page: number | undefined;
    pageSize: number | undefined;
    tagSetName: string | undefined;
}
async function getPaginatedTagSets(repository: Repository, { page = 1, pageSize = defaultPageSize, tagSetName = "" }: GetPaginatedTagSetsOptions) {
    const tagSets = repository.TagSets.list({
        skip: (page - 1) * pageSize,
        take: pageSize,
        partialName: tagSetName,
    });
    const tagSetsStats = repository.TagSets.list({ skip: 0, take: 0 });
    return {
        tagSets: await tagSets,
        hasTagSets: (await tagSetsStats).TotalResults > 0,
    };
}
function usePagedTagSetsQuery(queryParams: TagSetsPageQueryParams, loaderData: TagSetsPageLoaderData) {
    return useQuery((repository) => getPaginatedTagSets(repository, { page: queryParams.page, pageSize: queryParams.pageSize, tagSetName: queryParams.tagSetName }), [queryParams.page, queryParams.pageSize, queryParams.tagSetName], "Get paginated tag sets", {
        initialResult: loaderData,
    });
}
function getPageActions(openReorderDialogClick: () => void): PageAction[] {
    const sortDialogPageAction: PageAction = {
        type: "button",
        buttonType: "secondary",
        hasPermissions: isAllowed({ permission: Permission.TagSetCreate }),
        label: "Reorder",
        onClick: openReorderDialogClick,
    };
    return [sortDialogPageAction];
}
function TagSetListItem({ tagSet, spaceId }: {
    tagSet: TagSetResource;
    spaceId: string;
}) {
    return (<ListItemLink href={links.editTagSetPage.generateUrl({ spaceId, tagSetId: tagSet.Id })}>
            <ListItemContent>
                <ListTitle>{tagSet.Name}</ListTitle>
                <div className={styles.helpText}>
                    <MarkdownDescription markup={tagSet.Description}/>
                </div>
                <div>
                    {sortBy(tagSet.Tags, (t) => t.SortOrder).map((t) => (<Tag tagName={t.Name} tagColor={t.Color} key={t.Id} description={t.Description} borderColor={themeTokens.color.border.primary}/>))}
                </div>
            </ListItemContent>
        </ListItemLink>);
}
function ContentBorder({ children }: {
    children: React.ReactNode;
}) {
    return <div className={contentBorderStyles}>{children}</div>;
}
const contentBorderStyles = css({
    border: `1px solid ${themeTokens.color.border.primary}`,
    borderRadius: borderRadius.medium,
});
